export interface ErrorState {
  type: 'validation' | 'auth' | 'network' | 'server';
  message: string;
  retryable: boolean;
}

const backendErrorMessages: Record<string, string> = {
  'EMAIL_EXISTS': 'An account with this email already exists. Please try logging in instead.',
  'USER_NOT_FOUND': 'Invalid email or password. Please check your credentials.',
  'INVALID_PASSWORD': 'Invalid email or password. Please check your credentials.',
  'USER_DISABLED': 'This account has been disabled. Please contact support.',
  'TOO_MANY_ATTEMPTS': 'Too many failed attempts. Please try again later.',
  'INVALID_EMAIL': 'Please enter a valid email address.',
  'WEAK_PASSWORD': 'Password is too weak. Use at least 6 characters.',
  'MISSING_CREDENTIALS': 'Please enter both email and password.',
  'MISSING_FIELDS': 'Please fill in all required fields.',
  'REGISTRATION_FAILED': 'Failed to create account. Please try again.',
  'LOGIN_FAILED': 'Login failed. Please check your credentials.',
  'GOOGLE_AUTH_FAILED': 'Google authentication failed. Please try again.',
};

const firebaseErrorMessages: Record<string, string> = {
  'auth/user-not-found': 'No account found with this email address.',
  'auth/wrong-password': 'Incorrect password. Please try again.',
  'auth/invalid-email': 'Please enter a valid email address.',
  'auth/user-disabled': 'This account has been disabled. Contact support.',
  'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
  'auth/network-request-failed': 'Connection error. Check your internet.',
  'auth/email-already-in-use': 'An account with this email already exists.',
  'auth/weak-password': 'Password is too weak. Use at least 8 characters.',
  'auth/operation-not-allowed': 'Email/password sign-in is disabled.',
  'auth/popup-closed-by-user': 'Sign-in was cancelled.',
  'auth/popup-blocked': 'Pop-up blocked. Please allow pop-ups and try again.',
  'auth/invalid-credential': 'Invalid login credentials. Please check and try again.',
  'auth/account-exists-with-different-credential': 'Account exists with different sign-in method.',
};

const validationErrors = {
  emailRequired: 'Email address is required.',
  emailInvalid: 'Please enter a valid email address.',
  passwordRequired: 'Password is required.',
  passwordTooShort: 'Password must be at least 8 characters.',
  passwordsNoMatch: 'Passwords do not match.',
  bothFieldsRequired: 'Please enter both email and password.',
};

const networkErrors = {
  offline: 'You appear to be offline. Check your connection.',
  timeout: 'Request timed out. Please try again.',
  serverUnreachable: 'Cannot reach server. Check your connection.',
};

export const handleAuthError = (error: any): ErrorState => {
  // Check for our specific API error structure first
  const apiErrorCode = error?.response?.data?.error?.code;
  if (apiErrorCode && backendErrorMessages[apiErrorCode]) {
    return {
      type: 'auth',
      message: backendErrorMessages[apiErrorCode],
      retryable: !['USER_DISABLED'].includes(apiErrorCode),
    };
  }

  const errorCode = error?.code;
  const errorMessage = error?.message || '';

  // Backend API error codes (new) - Keep as a fallback
  if (errorCode && backendErrorMessages[errorCode]) {
    return {
      type: 'auth',
      message: backendErrorMessages[errorCode],
      retryable: !['USER_DISABLED'].includes(errorCode),
    };
  }

  // Firebase authentication errors (legacy)
  if (errorCode && firebaseErrorMessages[errorCode]) {
    return {
      type: 'auth',
      message: firebaseErrorMessages[errorCode],
      retryable: !['auth/user-disabled', 'auth/operation-not-allowed'].includes(errorCode),
    };
  }

  // If error message already user-friendly (from auth service), use it directly
  if (errorMessage && !errorMessage.includes('Request failed') && !errorMessage.includes('status code')) {
    return {
      type: 'auth',
      message: errorMessage,
      retryable: true,
    };
  }

  // Network errors
  if (errorMessage.includes('network') || errorMessage.includes('Network')) {
    return {
      type: 'network',
      message: networkErrors.serverUnreachable,
      retryable: true,
    };
  }

  // Generic fallback
  return {
    type: 'server',
    message: 'Something went wrong. Please try again.',
    retryable: true,
  };
};

export const handleValidationError = (type: keyof typeof validationErrors): ErrorState => {
  return {
    type: 'validation',
    message: validationErrors[type],
    retryable: false,
  };
};

export const formatErrorMessage = (errorState: ErrorState): string => {
  return errorState.message;
};
